<template>
  <v-card
    class="mb-2"
    elevation="1"
  >
    <!-- Title row -->
    <v-card-title class="bg-primary-lighten-1 d-flex flex-column flex-sm-row align-sm-center justify-space-between pa-3">
      <div class="d-flex align-center mb-2 mb-md-0">
        <v-icon
          :icon="`fa fa-${task.category?.icon ?? 'clipboard-check'}`"
          color="primary"
          size="small"
          class="me-2"
        />
        <div class="text-title-medium text-primary text-wrap">
          #{{ task.id }} - {{ task.title }}
        </div>
      </div>
      <div class="d-flex align-center flex-shrink-0">
        <v-btn
          v-if="mode == 'index'"
          v-tooltip="t('global.view')"
          color="primary"
          class="me-1"
          size="small"
          :to="`/tasks/${task.id}`"
        >
          <v-icon icon="fas fa-eye" />
        </v-btn>
        <task-edition-dialog
          v-tooltip="t('actions.edit')"
          :model-id="task.id"
          mode="edit"
          size="small"
          @reload="emit('reload')"
        />
        <deletion-dialog
          v-if="can('manage_tasks')"
          model-name="Task"
          :delete-url="`/api/tasks/${task.id}`"
          size="small"
          @model:deleted="onDeleted"
        />
      </div>
    </v-card-title>

    <!-- Status / category / due-date chips -->
    <div class="bg-light-grey px-3 py-2 d-flex flex-wrap gap-2">
      <v-chip
        :color="getStatusBadgeClass(task.status)"
        size="small"
        :class="{ 'text-font': task.status === 'completed' }"
        class="me-2"
      >
        {{ t(`states.${task.status}`) }}
        <span v-if="task.status === 'completed' && task.complete_date">
          &nbsp;{{ toLongDateWithDay(task.complete_date) }}
        </span>
      </v-chip>
      <v-chip
        v-if="task.category?.name"
        color="secondary"
        class="me-2"
        size="small"
        :text="task.category.name"
      />
      <v-chip
        v-if="task.due_date && task.status !== 'completed'"
        size="small"
        class="me-2"
        :color="isBeforeToday(task.due_date) ? 'error' : 'warning'"
        :prepend-icon="isBeforeToday(task.due_date) ? 'fas fa-circle-exclamation' : 'fas fa-calendar'"
      >
        {{ t('task.due') }}: {{ toLongDateWithDay(task.due_date) }}
      </v-chip>
      <v-chip
        size="small"
        variant="tonal"
        class="me-2"
        color="primary"
        prepend-icon="far fa-comments"
      >
        {{ t('task.comment', { count: task.comments?.length ?? 0 }, task.comments?.length ?? 0) }}
      </v-chip>
    </div>

    <v-card-text class="pa-3">
      <!-- Store -->
      <div class="d-flex align-start gap-2 mb-3">
        <v-icon
          icon="fas fa-warehouse"
          size="small"
          class="me-2"
          color="medium-emphasis"
        />
        <div class="text-body-medium text-medium-emphasis">
          {{ task.store?.name }}
        </div>
      </div>

      <v-divider class="mb-3" />

      <!-- Assignee + metadata -->
      <div class="d-flex align-start gap-3">
        <user-avatar
          v-if="task.assignee"
          :user="task.assignee"
          size="40"
          class="flex-shrink-0 me-2"
        />

        <div class="d-flex flex-column gap-1 flex-grow-1 min-width-0">
          <div
            v-if="task.assignee"
            class="d-flex flex-column"
          >
            <span class="text-body-large font-weight-medium ms-1">
              {{ task.assignee.full_name }}
            </span>
            <phone-number
              size="x-small"
              class-name="text-body-medium"
              :number="task.assignee.mobile_phone"
            />
          </div>

          <div class="d-flex align-center flex-wrap gap-2 ms-1 mt-1">
            <span class="text-body-medium text-medium-emphasis">
              <i18n-t keypath="task.opened_by">
                <template #date>
                  {{ isInFutureByAtLeast(task.created_at)
                    ? t('global.on_date', { date: toLongDateWithDayAndTime(task.created_at) })
                    : fromNow(task.created_at) }}
                </template>
                <template #user>
                  <a
                    v-if="task.reporter"
                    class="mx-1"
                    :href="`tel:${task.reporter.mobile_phone}`"
                  >{{ task.reporter.name }}</a>
                  <span v-else> {{ t('global.unknown') }} </span>
                </template>
              </i18n-t>
            </span>
          </div>
        </div>
      </div>
    </v-card-text>
  </v-card>
</template>

<script setup lang="ts">
import { useI18n } from 'vue-i18n';
import UserAvatar from '@/components/images/UserAvatar.vue';
import { useDate, usePermissions } from '@/composables';
import DeletionDialog from '../crud/DeletionDialog.vue';
import TaskEditionDialog from './TaskEditionDialog.vue';
import { useTaskUtilities } from './useTaskUtilities';

defineProps<{
  task: App.Models.Task;
  mode: 'show' | 'index';
}>();

const emit = defineEmits(['reload', 'model:deleted']);

const { t } = useI18n();
const { can } = usePermissions();
const { getStatusBadgeClass } = useTaskUtilities();
const {
  isInFutureByAtLeast,
  toLongDateWithDayAndTime,
  fromNow,
  isBeforeToday,
  toLongDateWithDay,
} = useDate();

function onDeleted() {
  emit('model:deleted');
  emit('reload');
}
</script>
